import type { BankItem } from "../../engine/bank-types";

/**
 * 核心概念 — what does 仁 / 禮 / 義 actually mean across the whole book?
 *
 * These items force a student to hold several passages at once, which is the
 * only way a concept in the Analects can be pinned down: Confucius never
 * defines 仁 abstractly, he answers differently every time he is asked.
 */
export const CONCEPT_ITEMS: BankItem[] = [
  {
    id: "cp-ren-1",
    type: "concept",
    refs: [323, 324, 325, 344, 365],
    concepts: ["ren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "孔子答仁因人而異，但有共同結構",
    stem: "顏淵問仁得「克己復禮」，仲弓問仁得「己所不欲，勿施於人」，司馬牛問仁得「其言也訒」，樊遲問仁得「愛人」。這些答案的共同點是？",
    options: [
      { id: "a", text: "都給可操作的著手處，各補其偏" },
      { id: "b", text: "答案互相矛盾，說明孔子對仁沒有定見", why: "9.4「毋必毋固」正說明他不給死定義；不同答案針對不同的人，不是自相矛盾。" },
      { id: "c", text: "都在說仁就是愛人，只是說法不同", why: "「其言也訒」與「克己復禮」都不能化約為愛人，各有具體所指。" },
      { id: "d", text: "都在強調禮的重要性", why: "只有顏淵那條扣住禮，其餘三條並不以禮為核心。" },
    ],
    answerId: "a",
    explanation: "司馬牛多言，就從說話下手；仲弓將從政，就從待人使民下手。仁不是一個定義，是每個人各自的下一步。",
  },
  {
    id: "cp-ren-2",
    type: "concept",
    refs: [68, 127, 180],
    concepts: ["ren", "xue"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "安仁與利仁的分別",
    stem: "「仁者安仁，知者利仁」——這兩種人差在哪裡？",
    options: [
      { id: "a", text: "一個安之若素，一個知其有利而為" },
      { id: "b", text: "仁者重情感，知者重理智", why: "分別不在情理，在仁是目的還是手段。" },
      { id: "c", text: "仁者是天生的，知者是學來的", why: "「我欲仁，斯仁至矣」表明仁可求；本章不談稟賦。" },
      { id: "d", text: "兩者是同一種人的兩個稱呼", why: "上句說「不仁者不可以久處約」，正是在區分不同層次。" },
    ],
    answerId: "a",
    explanation: "配合 6.20「知之者不如好之者，好之者不如樂之者」讀：利仁還需要理由支撐，安仁已經不需要——所以只有安仁的人能「久處約，長處樂」。",
  },
  {
    id: "cp-li-1",
    type: "concept",
    refs: [43, 44, 487, 12],
    concepts: ["li", "ren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "禮與仁的依存關係",
    stem: "「人而不仁，如禮何？人而不仁，如樂何？」這句話確立了禮與仁的什麼關係？",
    options: [
      { id: "a", text: "仁是禮樂的內在根據，無仁則成空殼" },
      { id: "b", text: "禮比仁更根本，先有禮才能有仁", why: "本句次序相反：仁不在，禮就無從談起。" },
      { id: "c", text: "禮與仁互不相干，各管一事", why: "「如禮何」正是說兩者不可分。" },
      { id: "d", text: "不仁的人不配行禮，應當禁止", why: "說的是禮對這種人失去意義，不是資格禁令。" },
    ],
    answerId: "a",
    explanation: "這是《八佾》一整篇批評僭禮的總綱：季氏八佾舞於庭，動作規格都在，缺的正是仁。禮的形式撐不住失去內容的重量。",
  },
  {
    id: "cp-li-2",
    type: "concept",
    refs: [12],
    concepts: ["li", "zhongyong"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "和為貴仍須以禮節之",
    stem: "有子說「禮之用，和為貴……有所不行，知和而和，不以禮節之，亦不可行也」。後半句補充了什麼？",
    options: [
      { id: "a", text: "一味求和而不以禮節之，同樣行不通" },
      { id: "b", text: "和諧最重要，禮可以隨之調整", why: "後半句正是防止把「和為貴」推到極端。" },
      { id: "c", text: "禮與和是兩套並行的制度", why: "和是禮運用時的可貴之處，不是另一套制度。" },
      { id: "d", text: "先王之道以和為唯一標準", why: "「小大由之」之後緊接「有所不行」，正是說有例外。" },
    ],
    answerId: "a",
    explanation: "前半句常被單獨引用成「以和為貴」。有子自己加了限制：知道和好、就只顧和好，沒有禮來節制，一樣走不通——這是對和諧主義的預先設防。",
  },
  {
    id: "cp-xue-1",
    type: "concept",
    refs: [1, 31, 27, 451, 518],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "學的完整循環",
    stem: "把 1.1 習、2.15 學思、2.11 溫故知新、19.5 日知其所亡合看，孔子的「學」包含哪幾個環節？",
    options: [
      { id: "a", text: "接收、演練、思考、出新、補缺的循環" },
      { id: "b", text: "背誦—記憶—考試，以掌握知識為目標", why: "全書無一處以記誦為學的目標；13.5 更以「雖多，亦奚以為」否定之。" },
      { id: "c", text: "讀書—做官—治國，以致用為唯一目的", why: "致用是檢驗，不是全部；「古之學者為己」明說學先為自己。" },
      { id: "d", text: "拜師—守成—傳承，重在不失師說", why: "「溫故而知新」正要求生出新的理解，不是守成。" },
    ],
    answerId: "a",
    explanation: "「日知其所亡，月無忘其所能」——子夏這句是全書對學習節奏最具體的描述：每天補上不會的，每月守住已會的。加減同時進行。",
  },
  {
    id: "cp-xue-2",
    type: "concept",
    refs: [400],
    concepts: ["xue", "junzi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "為己之學與為人之學",
    stem: "「古之學者為己，今之學者為人」——「為己」與「為人」的分別是？",
    options: [
      { id: "a", text: "為充實自己而學，或為給別人看而學" },
      { id: "b", text: "為己是自私，為人是利他，孔子讚後者", why: "語氣是揚古抑今，孔子讚的是「為己」。" },
      { id: "c", text: "為己是自學，為人是從師", why: "與學習方式無關，講的是動機。" },
      { id: "d", text: "為己是謀生，為人是服務社會", why: "「為人」在此指博取他人認可，不是服務。" },
    ],
    answerId: "a",
    explanation: "這句話極鋒利：學習一旦以他人的眼光為目標，內容就會被表演取代。它也解釋了為什麼孔子反覆說「不患人之不己知」。",
  },
  {
    id: "cp-junzi-1",
    type: "concept",
    refs: [82, 77, 30, 441, 372],
    concepts: ["junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "君子小人的對舉是一組判準",
    stem: "《論語》中君子小人多次對舉：喻於義／喻於利、懷德／懷土、周而不比／比而不周、求諸己／求諸人、泰而不驕／驕而不泰。這些對舉的共同結構是？",
    options: [
      { id: "a", text: "同一情境下向內向義與向外向利之別" },
      { id: "b", text: "描述兩個階級的生活方式差異", why: "「懷土」「懷惠」若指階級，便無從解釋「求諸己」這類純內在的分別。" },
      { id: "c", text: "君子的美德清單與小人的惡行清單", why: "它們是成對的，不是兩張獨立清單——每一組講的都是同一件事的兩種做法。" },
      { id: "d", text: "孔子對弟子與外人的不同要求", why: "君子小人不指身分歸屬，指行事取向。" },
    ],
    answerId: "a",
    explanation: "把這些對舉排在一起，君子就不再是模糊的褒詞，而是一套可檢查的判準：遇事你先算什麼、往哪裡找原因、靠不靠攏誰。",
  },
  {
    id: "cp-xiao-1",
    type: "concept",
    refs: [21, 22, 23, 24],
    concepts: ["xiao", "jiaoxue"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "四問孝的差異是因材施教",
    stem: "《為政》連續四人問孝，答案是「無違」「父母唯其疾之憂」「敬」「色難」。這種差異最合理的解釋是？",
    options: [
      { id: "a", text: "各補其缺，四答合起來才是全貌" },
      { id: "b", text: "孔子對孝的看法在不斷改變", why: "四章同在一篇，且無時間先後線索；11.22 的同問異答已提供了現成的解釋模式。" },
      { id: "c", text: "四人身分不同，孝的標準因階級而異", why: "所答皆為心意與態度，與身分無涉。" },
      { id: "d", text: "這是編者收集的不同傳聞，未必都可靠", why: "四章結構整齊且互補，正是有意編排的痕跡。" },
    ],
    answerId: "a",
    explanation: "孟懿子權位高，所以說「無違（於禮）」；孟武伯是貴族子弟，所以說別讓父母為你擔憂；子游能養而少敬，子夏能敬而少和色。四答各補一處。",
  },
  {
    id: "cp-shu-1",
    type: "concept",
    refs: [81, 444, 324],
    concepts: ["shu", "ren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "一貫之道的地位",
    stem: "「吾道一以貫之」，曾子解為「忠恕而已矣」。這條「一貫之道」在孔子思想中的地位是？",
    options: [
      { id: "a", text: "貫穿諸教的方法：由盡己與推己推出" },
      { id: "b", text: "是孔子晚年才提出的新學說", why: "章中無時間線索；且忠恕的內容散見全書各處。" },
      { id: "c", text: "是曾子個人的理解，孔子並不承認", why: "孔子說完便出，門人問曾子，曾子的解說被編入正文，是被承認的。" },
      { id: "d", text: "指學問要博而後約，與待人無關", why: "曾子明說是「忠恕」，落在待人接物上。" },
    ],
    answerId: "a",
    explanation: "15.3 孔子問子貢「女以予為多學而識之者與？」自答「非也，予一以貫之」——他兩次否認自己靠博聞取勝，強調有一條線把一切串起來。",
  },
  {
    id: "cp-zheng-1",
    type: "concept",
    refs: [17, 19, 339, 352],
    concepts: ["zheng"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "為政以德的機制",
    stem: "「為政以德，譬如北辰居其所而眾星共之」——這個比喻在說明什麼機制？",
    options: [
      { id: "a", text: "己身端正即成軸心，不待號令驅動" },
      { id: "b", text: "君主應當高高在上，與百姓保持距離", why: "北辰之喻取其居所不動而眾星環繞，重點在「居其所」的定與正，不在高下。" },
      { id: "c", text: "德治比法治更省事，可以什麼都不做", why: "「無為而治」另有其章（15.5），此處說的是端正自己這件事本身要做到。" },
      { id: "d", text: "百姓像星辰一樣需要被指引方向", why: "喻體的重點在北辰不動，不在指引。" },
    ],
    answerId: "a",
    explanation: "13.6「其身正，不令而行」是這個比喻的散文版。孔子講的不是道德姿態，是效力來源：命令的分量取決於發令者本身。",
  },
  {
    id: "cp-zhengming-1",
    type: "concept",
    refs: [349],
    concepts: ["zhengming", "zheng", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "正名的因果鏈",
    stem: "「名不正，則言不順；言不順，則事不成；事不成，則禮樂不興……」這條推論鏈說明正名為何優先？",
    options: [
      { id: "a", text: "名分是全鏈起點，缺它則後環皆失據" },
      { id: "b", text: "說明孔子重視語言修辭的準確", why: "「言不順」指說話失去依據，不是修辭問題。" },
      { id: "c", text: "說明禮樂比刑罰重要", why: "禮樂與刑罰都在鏈條中段，不是本章的論點。" },
      { id: "d", text: "說明子路的質疑是對的，正名確實迂闊", why: "整條推論正是孔子用來回應子路「子之迂也」的。" },
    ],
    answerId: "a",
    explanation: "子路嫌迂，孔子就把鏈條一環一環拆給他看，直到「民無所措手足」——名分不對，最後受害的是最底層的人。這使正名從概念變成了現實問題。",
  },
  {
    id: "cp-zhongyong-1",
    type: "concept",
    refs: [149, 286, 367],
    concepts: ["zhongyong"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "中庸的定位機制",
    stem: "中庸要求「恰好」。這個「恰好」由什麼決定？",
    options: [
      { id: "a", text: "由事情本身的分寸決定，因事而異" },
      { id: "b", text: "由兩個極端的中點決定，取平均即可", why: "若是算術中點，11.16「過猶不及」就只需比較誰更靠近中間，孔子卻拒絕排序。" },
      { id: "c", text: "由多數人的做法決定", why: "13.24 明說鄉人皆好之未可，多數不是判準。" },
      { id: "d", text: "由禮的條文明確規定", why: "禮提供分寸的參照，但 9.3 顯示禮本身也需要隨事斟酌。" },
    ],
    answerId: "a",
    explanation: "13.21「不得中行而與之，必也狂狷乎」——連孔子都承認中行難得，退而取狂狷。這說明中不是安全的中間，而是難以拿捏的準確。",
  },
  {
    id: "cp-tianming-1",
    type: "concept",
    refs: [20, 470, 541, 411],
    concepts: ["tianming"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "知天命的內容",
    stem: "「五十而知天命」「君子有三畏：畏天命」「不知命，無以為君子」——孔子的「知命」主要指什麼？",
    options: [
      { id: "a", text: "認清力所不及處，盡力而不以成敗自評" },
      { id: "b", text: "能夠預知未來的吉凶禍福", why: "他「不語怪力亂神」，也不以占驗為事，知命與預知無關。" },
      { id: "c", text: "相信一切都是註定的，努力沒有意義", why: "「知其不可而為之」正是反證。" },
      { id: "d", text: "知道自己的使命是恢復周禮", why: "使命感確實有（9.5「文不在茲乎」），但「命」的所指更廣。" },
    ],
    answerId: "a",
    explanation: "知命的作用是劃界：界內全力，界外不怨。所以他能「不怨天，不尤人」——這不是認輸，是把有限的力氣用在真正能用的地方。",
  },
  {
    id: "cp-wenzhi-1",
    type: "concept",
    refs: [138, 330, 48],
    concepts: ["wenzhi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "文質關係的三個層次",
    stem: "6.18「文質彬彬」、12.8 子貢駁棘子成、3.8「繪事後素」——三章合看，文與質的關係是？",
    options: [
      { id: "a", text: "質雖在先，仍須文來成全，終須相稱" },
      { id: "b", text: "質永遠比文重要，文可有可無", why: "12.8 子貢正是駁斥這一主張：去掉文彩，虎豹之皮與犬羊之皮就分不出來。" },
      { id: "c", text: "文比質重要，因為人先看到外表", why: "3.8「繪事後素」明確把素（質）放在前。" },
      { id: "d", text: "文與質是同一件事的兩個名稱", why: "三章都在區分兩者，並討論其配比。" },
    ],
    answerId: "a",
    explanation: "有先後（質先），但無主次（相稱）。這兩點看似衝突，其實是同一件事的兩個面向——這也是這組概念最容易被各取一半的地方。",
  },
  {
    id: "cp-shijiao-1",
    type: "concept",
    refs: [485, 196, 18],
    concepts: ["shijiao", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "興於詩立於禮成於樂的次序",
    stem: "「興於《詩》，立於禮，成於樂」——這三句描述的是？",
    options: [
      { id: "a", text: "由詩感發、由禮站定、由樂完成" },
      { id: "b", text: "三門課程的教學順序，先學詩再學禮最後學樂", why: "「興、立、成」說的是人身上發生了什麼，不是課表。" },
      { id: "c", text: "三種文獻的重要性排序", why: "三字都是動詞，講的是作用不是排名。" },
      { id: "d", text: "詩禮樂三者可以互相替代", why: "各有不可替代的作用，故分列三句。" },
    ],
    answerId: "a",
    explanation: "興是把人打動、發動起來；立是給行為一個站得住的形式；成是讓一切變得順暢自然。這是一條從感受到行為再到境界的完整路徑。",
  },
  {
    id: "cp-chushi-1",
    type: "concept",
    refs: [201, 427, 113],
    concepts: ["chushi", "zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "出處的判準不是安全",
    stem: "「邦有道則見，無道則隱」「邦有道，如矢；邦無道，如矢」（史魚）「邦有道則知；邦無道則愚」（甯武子）——孔子對三種取態的評價說明？",
    options: [
      { id: "a", text: "無單一正解，判準在能否守住道" }, { id: "e", text: "凡有道則出、無道則隱，別無他途", why: "史魚無道時仍如矢而不隱，甯武子則佯愚以自全，兩者都受稱許。" },
      { id: "b", text: "孔子主張一律隱退，避免涉險", why: "他稱史魚「邦無道，如矢」為直，正是不退的一種。" },
      { id: "c", text: "孔子主張一律堅持直道，不可變通", why: "他說甯武子的「愚」不可及，可見變通也在其中。" },
      { id: "d", text: "孔子的評價前後矛盾，無法統一", why: "統一之處在於守道，只是守法不同。" },
    ],
    answerId: "a",
    explanation: "「其知可及也，其愚不可及也」——裝糊塗以保全自身與道，比聰明更難。孔子看的不是進退的姿態，是進退中有沒有守住東西。",
  },
  {
    id: "cp-zhiren-1",
    type: "concept",
    refs: [443, 448, 405],
    concepts: ["zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "不以言舉人不以人廢言",
    stem: "「君子不以言舉人，不以人廢言」防的是哪兩種錯誤？",
    options: [
      { id: "a", text: "因言舉人，以及因人廢言" }, { id: "e", text: "因親疏而取捨，以及因好惡而增損", why: "本句的兩端是言與人，不涉親疏好惡。" },
      { id: "b", text: "因為地位低就不聽；因為地位高就照聽", why: "本句的兩端是言與人，不是地位。" },
      { id: "c", text: "輕信傳聞；輕易表態", why: "與傳聞無關，講的是言與人之間的推論方向。" },
      { id: "d", text: "誇獎過度；批評過度", why: "講的是判斷的依據錯置，不是程度。" },
    ],
    answerId: "a",
    explanation: "兩個方向的錯誤是對稱的：把言的優劣搬到人身上，或把人的優劣搬到言身上。孔子要求兩件事分別評——這比只防其中一頭難得多。",
  },
  {
    id: "cp-xin-1",
    type: "concept",
    refs: [38, 329, 13],
    concepts: ["xin"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "信的比喻與其分量",
    stem: "「人而無信，不知其可也。大車無輗，小車無軏，其何以行之哉？」這個比喻的要點是？",
    options: [
      { id: "a", text: "信是連接的關鍵，缺它則各部接不起來" },
      { id: "b", text: "信像車輪，是前進的動力", why: "輗軏是轅與衡相接的關鍵榫頭，取義在「連接」而非動力。" },
      { id: "c", text: "信是車的裝飾，有了更體面", why: "沒有輗軏車根本不能行，並非裝飾。" },
      { id: "d", text: "大車小車各有其信，標準不同", why: "大車小車並舉是為說明無論大小皆然。" },
    ],
    answerId: "a",
    explanation: "選這個零件作比喻很精確：輗軏不大、不顯眼、也不出力，但沒有它，牛馬與車廂就是兩件互不相干的東西。人與人之間也是。",
  },
  {
    id: "cp-yili-1",
    type: "concept",
    refs: [76, 82, 71, 499],
    concepts: ["yili", "junzi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "義是判準而非教條",
    stem: "「義之與比」「義以為上」「義以為質」——「義」在這些句子中扮演的角色是？",
    options: [
      { id: "a", text: "最終判準，勇信禮皆由它定其分寸" },
      { id: "b", text: "一套具體的行為規範清單", why: "若是清單，就不需要「無適也，無莫也」這種因事而定的說法。" },
      { id: "c", text: "與利對立的道德情感", why: "義是判斷而非情感；且它也判斷勇與信，不只針對利。" },
      { id: "d", text: "君子的專屬德行，小人不必講義", why: "17.23 說小人有勇無義為盜，正是拿義來衡量所有人。" },
    ],
    answerId: "a",
    explanation: "17.23 最能說明它的地位：勇是好的，但「君子有勇而無義為亂，小人有勇而無義為盜」——義不是德目之一，是給所有德目定方向的那一個。",
  },
  {
    id: "cp-jiaoxue-1",
    type: "concept",
    refs: [459, 157, 141, 179],
    concepts: ["jiaoxue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "有教無類的實踐證據",
    stem: "哪些記載可以支持「有教無類」不只是口號？",
    options: [
      { id: "a", text: "束脩以上未嘗無誨，互鄉童子亦與其進" },
      { id: "b", text: "「中人以下，不可以語上也」", why: "這講的是教法分層，不是入學門檻，不能用來證明無類。" },
      { id: "c", text: "「唯上知與下愚不移」", why: "這一句反而常被引來質疑有教無類，不能作為支持證據。" },
      { id: "d", text: "「不憤不啟，不悱不發」", why: "這說的是啟發時機，與收不收這個學生無關。" },
    ],
    answerId: "a",
    explanation: "找證據要找對層面：束脩是極低的門檻，互鄉童子是名聲不好的地方來的人——一個講收費，一個講出身，兩件事都指向「不設限」。",
  },
  {
    id: "cp-ren-3",
    type: "concept",
    refs: [43, 429, 456, 195],
    concepts: ["ren", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "仁的分量與其代價",
    stem: "「當仁，不讓於師」「無求生以害仁」「仁以為己任」——這些話共同顯示仁在價值序列中的位置是？",
    options: [
      { id: "a", text: "高於師道與生命，不可再讓步" },
      { id: "b", text: "與禮並列，兩者同等重要", why: "3.3「人而不仁，如禮何」已把仁置於禮之先。" },
      { id: "c", text: "是眾多德目之一，視情況權衡", why: "「不讓於師」「無求生以害仁」都是不權衡的表述。" },
      { id: "d", text: "只在極端處境下才需要考慮", why: "「仁以為己任」是終身的日常任務，不限極端。" },
    ],
    answerId: "a",
    explanation: "「當仁，不讓於師」尤其驚人：在一個極重師道的體系裡，孔子親口說碰到仁的事不必讓老師。這句話標定了仁的絕對位置。",
  },
  {
    id: "cp-zhiren-2",
    type: "concept",
    refs: [73],
    concepts: ["zhiren", "ren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "觀過知仁的方法",
    stem: "「人之過也，各於其黨。觀過，斯知仁矣」——這句話提供了什麼識人方法？",
    options: [
      { id: "a", text: "過錯各有其類，觀其所錯即知其人"},
      { id: "b", text: "看一個人有沒有過錯，就知道他仁不仁", why: "重點不在有無過錯，在過錯的性質與類型。" },
      { id: "c", text: "應當寬容別人的過錯，這才是仁", why: "「觀」是觀察方法，不是態度勸告。" },
      { id: "d", text: "同黨的人會互相包庇過錯", why: "「黨」在此指類別，不指黨派。" },
    ],
    answerId: "a",
    explanation: "很聰明的觀察：一個人為情義而犯的錯，和為私利而犯的錯，性質完全不同。過錯比成就更能透露一個人真正在乎什麼。",
  },
  {
    id: "cp-li-3",
    type: "concept",
    refs: [212],
    concepts: ["li", "zhongyong"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "禮的可變與不可變",
    stem: "9.3 中孔子在麻冕一事上「從眾」，在拜下一事上「違眾」。他的取捨標準是？",
    options: [
      { id: "a", text: "器物省儉可隨俗，上下之敬不可隨" },
      { id: "b", text: "凡是多數人的做法都應該跟從", why: "他在拜下一事上明白違眾，可見從眾不是原則。" },
      { id: "c", text: "凡是古制都不可更改", why: "麻冕改純他就接受了，古制並非一律不可改。" },
      { id: "d", text: "他的取捨隨心情而定，沒有一定標準", why: "兩例對舉正是為了顯示標準，不是隨意。" },
    ],
    answerId: "a",
    explanation: "這一章是禮學最好的案例：孔子親自示範哪些能改、哪些不能。判準不在新舊多寡，在這個形式還撐不撐得住它本來要表達的敬。",
  },
  {
    id: "cp-xue-3",
    type: "concept",
    refs: [239],
    concepts: ["xue", "zhongyong"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "共學到能權的四階",
    stem: "「可與共學，未可與適道；可與適道，未可與立；可與立，未可與權」——最高的一階「權」指什麼？",
    options: [
      { id: "a", text: "能因事權衡變通而仍不失其原則" },
      { id: "b", text: "掌握權力，能夠推行主張", why: "「權」在此是權衡稱量之義，非權位。" },
      { id: "c", text: "有權威，能讓別人聽從", why: "四階說的是為學者自身的程度，不涉他人。" },
      { id: "d", text: "能夠變通，不必再守原則", why: "權是在守得住（立）之後才談的，不是取消原則。" },
    ],
    answerId: "a",
    explanation: "次序很要緊：先能一起學，再能同走一條路，再能站得住，最後才談變通。不曾「立」過的人談權變，那不是權，是沒有原則。",
  },
  {
    id: "cp-zheng-2",
    type: "concept",
    refs: [540, 341],
    concepts: ["zheng", "ren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "尊五美屏四惡",
    stem: "「君子惠而不費，勞而不怨，欲而不貪，泰而不驕，威而不猛」——這五美的共同句式說明什麼？",
    options: [
      { id: "a", text: "取其品質而去其流弊，皆有分寸" },
      { id: "b", text: "五種美德互不相干，需要分別修養", why: "五句同一結構「A 而不 B」，正說明它們是同一種思路的五次應用。" },
      { id: "c", text: "五美是對百姓的要求", why: "首句「君子」點明對象是治理者。" },
      { id: "d", text: "說明治理不需要威嚴", why: "「威而不猛」保留了威，去掉的是猛。" },
    ],
    answerId: "a",
    explanation: "惠、勞、欲、泰、威本身都不壞，壞在過了頭就成費、怨、貪、驕、猛。這五句是「過猶不及」在政事上的具體展開。",
  },
  {
    id: "cp-shu-2",
    type: "concept",
    refs: [435, 441],
    concepts: ["shu", "junzi"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "厚責己薄責人",
    stem: "「躬自厚而薄責於人，則遠怨矣」——這句話解決的是什麼問題？",
    options: [
      { id: "a", text: "怨多起於兩把尺，調轉比例即少衝突" },
      { id: "b", text: "如何讓別人不怨恨自己，重在忍讓", why: "方法不是忍讓，是調整責備的分配。" },
      { id: "c", text: "如何提高自己的道德水準", why: "落點在「遠怨」，是人際問題而非修養排名。" },
      { id: "d", text: "如何管理下屬，減少抱怨", why: "本句對象是一般的人際關係，不限於上下。" },
    ],
    answerId: "a",
    explanation: "這是很務實的一條：怨的產生往往不是因為誰做錯了，是因為兩把尺不一樣。把嚴的那把留給自己，衝突自然少。",
  },
  {
    id: "cp-tianming-2",
    type: "concept",
    refs: [142, 171, 185],
    concepts: ["tianming", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "孔子對超越者的一貫姿態",
    stem: "「敬鬼神而遠之」「不語怪力亂神」「丘之禱久矣」——三處合看，孔子的態度可概括為？",
    options: [
      { id: "a", text: "承認而不依賴，敬之而不以之代人事" },
      { id: "b", text: "堅定的無神論，否認鬼神存在", why: "他重祭祀，說「祭如在」，並未作存在與否的斷言。" },
      { id: "c", text: "虔誠的信仰者，凡事訴諸神明", why: "「遠之」與「不語」都在拉開距離。" },
      { id: "d", text: "態度前後不一，隨場合而變", why: "三處指向同一姿態，只是場合不同。" },
    ],
    answerId: "a",
    explanation: "子路請禱，他答「丘之禱久矣」——最漂亮的一句。不是拒絕禱告，是說平日的行事本身就是禱告，不必臨時另求。",
  },
];
